<script setup lang="ts">
import { ref, onMounted, onBeforeUnmount, watch, computed } from "vue";
import PerfectScrollbar from "perfect-scrollbar";
import "perfect-scrollbar/css/perfect-scrollbar.css";
import IconChevronUp from "@/assets/icons/IconChevronUp.vue";
import { paramsList } from "@/composables/list/params.list";
import { citiesList } from "@/composables/list/cities.list";
import {useStore} from "@/stores";
import TableLoader from "@/components/ui/loaders/TableLoader.vue";
import TableEmptyData from "@/components/ui/loaders/TableEmptyData.vue";
import type {FileData, StatisticsData} from "@/types/files";
import type {DistrictKey} from "@/composables/functions/getDistrictName";
import type {RegionKey} from "@/composables/functions/getRegionName";
import {districtList} from "@/composables/list/district.list";
import {regionToDistrictList} from "@/composables/list/regionToDistrict.list";

// Types
type ParameterValue = StatisticsData;

interface MainTableProps {
  items: FileData[];
  paramsSearchQuery: string;
  selectedRegions: string[];
  loading: boolean;
  selectedDistrictKey?: DistrictKey | null;
  selectedRegionKey?: RegionKey | null;
}

interface Column {
  key: string;
  label: string;
}

// store
const mainStore = useStore();

// Props
const props = defineProps<MainTableProps>();

// Список колонок. БЕЗ Прогноза.
const COLUMNS: Column[] = [
  { key: "param_name", label: "Название параметра" },
  { key: "date_period", label: "Отчетный период" },
  { key: "appg", label: "АППГ" },
  { key: "appg_percent", label: "% к АППГ" },
];

// Конфигурация вложенных параметров
const NESTED_PARAMS: Record<string, string[]> = {
  cases_accepted: [
    "cases_transferred_without_indictment",
    "percent_transferred_without_indictment",
    "cases_transferred_with_indictment",
    "percent_transferred_with_indictment",
  ],
  percent_cases_returned_to_prosecutor: [
      "cases_returned_by_prosecutor",
      "percent_cases_returned_by_prosecutor",
      "cases_returned_by_court_article_237"
  ],
  cases_completed_overtime: [
      "cases_resumed_from_suspended"
  ]
};

// Refs
const scrollContainer = ref<HTMLElement | null>(null);
const hasScroll = ref(false);
const expandedRows = ref<Set<string>>(new Set());
const expandedParams = ref<Set<string>>(new Set());
const expandedDistricts = ref<Set<DistrictKey>>(new Set());

const isRowExpanded = (rowId: string) => expandedRows.value.has(rowId);
const isParamExpanded = (paramId: string) => expandedParams.value.has(paramId);
const isDistrictExpanded = (districtKey: DistrictKey) => expandedDistricts.value.has(districtKey);

// Режим отображения с федеральными округами
const showDistrictsMode = computed(() => {
  return !props.selectedDistrictKey && !props.selectedRegionKey;
});

// Фильтрация регионов по округу
const getRegionsForDistrict = (districtKey: DistrictKey): FileData[] => {
  return props.items.filter(item =>
    regionToDistrictList[item.region as keyof typeof regionToDistrictList] === districtKey
  );
};

// Функция. Раскрытие списка регионов
const toggleRowExpansion = (rowId: string) => {
  const newSet = new Set(expandedRows.value);
  if (newSet.has(rowId)) {
    newSet.delete(rowId);
  } else {
    newSet.add(rowId);
  }
  expandedRows.value = newSet;
  updateScroll();
};

// Функция. Раскрытия списка вложенных параметров
const toggleParamExpansion = (paramId: string) => {
  const newSet = new Set(expandedParams.value);
  if (newSet.has(paramId)) {
    newSet.delete(paramId);
  } else {
    newSet.add(paramId);
  }
  expandedParams.value = newSet;
  updateScroll();
};

// Функция. Раскрытие федерального округа
const toggleDistrictExpansion = (districtKey: DistrictKey) => {
  const newSet = new Set(expandedDistricts.value);
  if (newSet.has(districtKey)) {
    newSet.delete(districtKey);
  } else {
    newSet.add(districtKey);
  }
  expandedDistricts.value = newSet;
  updateScroll();
};

// Проверка, есть ли вложенные параметры.
const hasNestedParams = (paramKey: string): boolean => {
  return paramKey in NESTED_PARAMS;
};

// Функция. Получения вложенных параметров.
const getNestedParams = (row: FileData, parentKey: string): Array<{ key: string; value: ParameterValue }> => {
  const nestedKeys = NESTED_PARAMS[parentKey] || [];
  return nestedKeys
      .map((key) => {
        const value = (row)[key];
        if (typeof value === "object" && value !== null) {
          return { key, value: value as ParameterValue };
        }
        return null;
      })
      .filter((item): item is { key: string; value: ParameterValue } => item !== null);
};

// Получение наименования региона по ключу
const getRegionName = (regionKey: string): string => {
  return citiesList[regionKey as keyof typeof citiesList] || regionKey;
};

// Получение наименования параметра по ключу
const getParameterName = (paramKey: string): string => {
  return paramsList[paramKey as keyof typeof paramsList] || paramKey;
};


const getRowParameters = (row: FileData): Array<{ key: string; value: ParameterValue }> => {
  // Собираем все вложенные параметры в один Set для быстрой проверки
  const nestedParamsSet = new Set<string>();
  Object.values(NESTED_PARAMS).forEach((nestedKeys) => {
    nestedKeys.forEach((key) => nestedParamsSet.add(key));
  });

  const allParams = Object.entries(row)
      .filter(([key, value]) => {
        // Исключаем вложенные параметры из основного списка
        if (nestedParamsSet.has(key)) return false;
        return typeof value === "object" && value !== null;
      })
      .map(([key, value]) => ({ key, value: value as ParameterValue }));

  // Фильтрация по поисковому запросу
  if (props.paramsSearchQuery && props.paramsSearchQuery.length > 2) {
    const query = props.paramsSearchQuery.toLowerCase().trim();
    return allParams.filter(param => {
      const paramName = getParameterName(param.key).toLowerCase();
      return paramName.includes(query);
    });
  }

  return allParams;
};

// Проверка, есть ли совпадения в параметрах строки
const hasMatchingParams = (row: FileData): boolean => {
  if (!props.paramsSearchQuery || props.paramsSearchQuery.length <= 2) {
    return true;
  }
  return getRowParameters(row).length > 0;
};

// PerfectScrollbar
const checkScrollPresence = () => {
  if (scrollContainer.value) {
    hasScroll.value = scrollContainer.value.scrollHeight > scrollContainer.value.clientHeight;
  }
};

const updateScroll = () => {
  setTimeout(() => {
    checkScrollPresence();
    ps?.update();
  }, 0);
};

let ps: PerfectScrollbar | null = null;
let resizeObserverInstance: ResizeObserver | null = null;

const initializeScrollbar = () => {
  if (!scrollContainer.value) return;

  ps = new PerfectScrollbar(scrollContainer.value, {
    wheelSpeed: 1,
    wheelPropagation: false,
    minScrollbarLength: 20,
  });

  checkScrollPresence();

  resizeObserverInstance = new ResizeObserver(() => {
    checkScrollPresence();
    ps?.update();
  });

  resizeObserverInstance.observe(scrollContainer.value);
};

const destroyScrollbar = () => {
  if (resizeObserverInstance) {
    resizeObserverInstance.disconnect();
    resizeObserverInstance = null;
  }

  if (ps) {
    ps.destroy();
    ps = null;
  }
};

// Автоматическое раскрытие строк при поиске > 2 символов
watch(() => props.paramsSearchQuery, (newQuery) => {
  if (newQuery && newQuery.length > 2) {
    // Раскрываем все строки, которые имеют совпадения
    const newExpandedRows = new Set<string>();
    props.items.forEach(row => {
      if (hasMatchingParams(row)) {
        newExpandedRows.add(row.file_id);
      }
    });
    expandedRows.value = newExpandedRows;
  } else if (newQuery.length === 0) {
    // Закрываем все строки при очистке поиска
    expandedRows.value = new Set();
  }

  updateScroll();
});

// Автоматическое раскрытие строк при обновлении данных (если выбрано >= 1 региона)
watch(() => props.items, () => {
  if (props.selectedRegions && props.selectedRegions.length >= 1) {
    // Раскрываем все строки при загрузке новых данных
    const newExpandedRows = new Set<string>();
    props.items.forEach(row => {
      newExpandedRows.add(row.file_id);
    });
    expandedRows.value = newExpandedRows;
    updateScroll();
  }
});

onMounted(() => {
  initializeScrollbar();
});

onBeforeUnmount(() => {
  destroyScrollbar();
});

// Обновление позиции скролла при измение элементов таблицы.
watch(() => props.items, () => {
  updateScroll();
});
</script>

<template>
  <div class="wrapper">
    <div class="main-table">
      <!-- Шапка таблицы -->
      <div
        :class="{ 'main-table__header--with-scroll': hasScroll, 'main-table__header--forecast': mainStore.forecastStatusDisplay }"
        class="main-table__header main-table__row main-table__row--header"
      >
        <div
          v-for="column in COLUMNS"
          :key="column.key"
          class="main-table__col main-table__col--header"
        >
          {{ column.label }}
        </div>
        <div
          v-if="mainStore.forecastStatusDisplay"
          :class="{'main-table__col--forecast-header': mainStore.forecastStatusDisplay}"
          class="main-table__col main-table__col--header"
        >
          Прогноз
        </div>
      </div>

      <!-- Основное содержимое таблицы -->
      <div
        ref="scrollContainer"
        class="main-table__body"
      >
        <!-- Режим с федеральными округами -->
        <template v-if="showDistrictsMode">
          <template
            v-for="(districtName, districtKey) in districtList"
            :key="districtKey"
          >
            <!-- Заголовок федерального округа -->
            <div
              class="main-table__row main-table__row--title"
              :class="{ 'main-table__row--with-scroll': hasScroll,
                        'main-table__row--expanded': isDistrictExpanded(districtKey as DistrictKey),
                        'main-table__row--title-forecast': mainStore.forecastStatusDisplay}"
            >
              <div class="main-table__col main-table__col--title">
                <button
                  class="expand-button"
                  :class="{ 'expand-button--expanded': isDistrictExpanded(districtKey as DistrictKey) }"
                  @click="toggleDistrictExpansion(districtKey as DistrictKey)"
                >
                  <IconChevronUp />
                </button>
                {{ districtName.full }}
              </div>
              <div
                v-if="mainStore.forecastStatusDisplay"
                :class="{'main-table__col--forecast': mainStore.forecastStatusDisplay}"
                class="main-table__col main-table__col--spacer"
              />
            </div>

            <!-- Регионы округа -->
            <template v-if="isDistrictExpanded(districtKey as DistrictKey)">
              <template
                v-for="row in getRegionsForDistrict(districtKey as DistrictKey)"
                :key="row.file_id"
              >
                <!-- Заголовок региона -->
                <div
                  class="main-table__row main-table__row--title main-table__row--title-nested"
                  :class="{ 'main-table__row--with-scroll': hasScroll,
                            'main-table__row--expanded': isRowExpanded(row.file_id),
                            'main-table__row--title-nested-forecast': mainStore.forecastStatusDisplay}"
                >
                  <div class="main-table__col main-table__col--spacer" />
                  <div class="main-table__col main-table__col--title">
                    <button
                      class="expand-button"
                      :class="{ 'expand-button--expanded': isRowExpanded(row.file_id) }"
                      @click="toggleRowExpansion(row.file_id)"
                    >
                      <IconChevronUp />
                    </button>
                    {{ getRegionName(row.region) }}
                  </div>
                  <div
                    v-if="mainStore.forecastStatusDisplay"
                    :class="{'main-table__col--forecast': mainStore.forecastStatusDisplay}"
                    class="main-table__col main-table__col--spacer"
                  />
                </div>

                <!-- Раскрывающиеся параметры региона -->
                <template v-if="isRowExpanded(row.file_id)">
                  <template
                    v-for="param in getRowParameters(row)"
                    :key="param.key"
                  >
                    <!-- Строка параметра -->
                    <div
                      class="main-table__row main-table__row--data main-table__row--data-district"
                      :class="{ 'main-table__row--with-scroll': hasScroll, 'main-table__row--data-district-forecast': mainStore.forecastStatusDisplay }"
                    >
                      <div class="main-table__col main-table__col--spacer" />
                      <div class="main-table__col main-table__col--spacer" />
                      <div
                        class="main-table__col"
                        :class="{ 'main-table__col--expandable': hasNestedParams(param.key) }"
                      >
                        <button
                          v-if="hasNestedParams(param.key)"
                          class="expand-button expand-button--nested"
                          :class="{ 'expand-button--expanded': isParamExpanded(`${row.file_id}-${param.key}`) }"
                          @click="toggleParamExpansion(`${row.file_id}-${param.key}`)"
                        >
                          <IconChevronUp />
                        </button>
                        <span>{{ getParameterName(param.key) }}</span>
                      </div>
                      <div class="main-table__col main-table__col--value">
                        {{ param.value.reporting_period }}
                      </div>
                      <div class="main-table__col main-table__col--value">
                        {{ param.value.previous_year }}
                      </div>
                      <div class="main-table__col main-table__col--value">
                        {{ param.value.percent_to_previous_year }}
                      </div>
                      <div
                        v-if="mainStore.forecastStatusDisplay"
                        :class="{'main-table__col--forecast': mainStore.forecastStatusDisplay}"
                        class="main-table__col main-table__col--value"
                      >
                        {{ mainStore.currentForecast.regions?.[row.region]?.[param.key].prediction_value ?? 0 }}
                      </div>
                    </div>

                    <!-- Вложенные параметры -->
                    <template v-if="hasNestedParams(param.key) && isParamExpanded(`${row.file_id}-${param.key}`)">
                      <div
                        v-for="nestedParam in getNestedParams(row, param.key)"
                        :key="nestedParam.key"
                        class="main-table__row main-table__row--data main-table__row--nested main-table__row--nested-district"
                        :class="{ 'main-table__row--with-scroll': hasScroll, 'main-table__row--nested-district-forecast': mainStore.forecastStatusDisplay }"
                      >
                        <div class="main-table__col main-table__col--spacer" />
                        <div class="main-table__col main-table__col--spacer" />
                        <div class="main-table__col main-table__col--spacer" />
                        <div class="main-table__col main-table__col--nested">
                          {{ getParameterName(nestedParam.key) }}
                        </div>
                        <div class="main-table__col main-table__col--value">
                          {{ nestedParam.value.reporting_period }}
                        </div>
                        <div class="main-table__col main-table__col--value">
                          {{ nestedParam.value.previous_year }}
                        </div>
                        <div class="main-table__col main-table__col--value">
                          {{ nestedParam.value.percent_to_previous_year }}
                        </div>
                        <div
                          v-if="mainStore.forecastStatusDisplay"
                          :class="{'main-table__col--forecast': mainStore.forecastStatusDisplay}"
                          class="main-table__col main-table__col--value"
                        >
                          {{ mainStore.currentForecast.regions?.[row.region]?.[nestedParam.key].prediction_value ?? 0 }}
                        </div>
                      </div>
                    </template>
                  </template>
                </template>
              </template>
            </template>
          </template>
        </template>

        <!-- Обычный режим (без округов) -->
        <template v-else>
          <template
            v-for="row in props.items"
            :key="row.file_id"
          >
            <!-- Заголовок региона -->
            <div
              class="main-table__row main-table__row--title"
              :class="{ 'main-table__row--with-scroll': hasScroll,
                        'main-table__row--expanded': isRowExpanded(row.file_id),
                        'main-table__row--title-forecast': mainStore.forecastStatusDisplay}"
            >
              <div class="main-table__col main-table__col--title">
                <button
                  class="expand-button"
                  :class="{ 'expand-button--expanded': isRowExpanded(row.file_id) }"
                  @click="toggleRowExpansion(row.file_id)"
                >
                  <IconChevronUp />
                </button>
                {{ getRegionName(row.region) }}
              </div>
              <div
                v-if="mainStore.forecastStatusDisplay"
                :class="{'main-table__col--forecast': mainStore.forecastStatusDisplay}"
                class="main-table__col main-table__col--spacer"
              />
            </div>

            <!-- Раскрывающиеся параметры -->
            <template v-if="isRowExpanded(row.file_id)">
              <template
                v-for="param in getRowParameters(row)"
                :key="param.key"
              >
                <!-- Строка параметра -->
                <div
                  class="main-table__row main-table__row--data"
                  :class="{ 'main-table__row--with-scroll': hasScroll, 'main-table__row--forecast': mainStore.forecastStatusDisplay }"
                >
                  <div class="main-table__col main-table__col--spacer" />
                  <div
                    class="main-table__col"
                    :class="{ 'main-table__col--expandable': hasNestedParams(param.key) }"
                  >
                    <button
                      v-if="hasNestedParams(param.key)"
                      class="expand-button expand-button--nested"
                      :class="{ 'expand-button--expanded': isParamExpanded(`${row.file_id}-${param.key}`) }"
                      @click="toggleParamExpansion(`${row.file_id}-${param.key}`)"
                    >
                      <IconChevronUp />
                    </button>
                    <span>{{ getParameterName(param.key) }}</span>
                  </div>
                  <div class="main-table__col main-table__col--value">
                    {{ param.value.reporting_period }}
                  </div>
                  <div class="main-table__col main-table__col--value">
                    {{ param.value.previous_year }}
                  </div>
                  <div class="main-table__col main-table__col--value">
                    {{ param.value.percent_to_previous_year }}
                  </div>
                  <div
                    v-if="mainStore.forecastStatusDisplay"
                    :class="{'main-table__col--forecast': mainStore.forecastStatusDisplay}"
                    class="main-table__col main-table__col--value"
                  >
                    {{ mainStore.currentForecast.regions?.[row.region]?.[param.key].prediction_value ?? 0 }}
                  </div>
                </div>

                <!-- Вложенные параметры -->
                <template v-if="hasNestedParams(param.key) && isParamExpanded(`${row.file_id}-${param.key}`)">
                  <div
                    v-for="nestedParam in getNestedParams(row, param.key)"
                    :key="nestedParam.key"
                    class="main-table__row main-table__row--data main-table__row--nested"
                    :class="{ 'main-table__row--with-scroll': hasScroll, 'main-table__row--forecast-nested': mainStore.forecastStatusDisplay }"
                  >
                    <div class="main-table__col main-table__col--spacer" />
                    <div class="main-table__col main-table__col--spacer" />
                    <div class="main-table__col main-table__col--nested">
                      {{ getParameterName(nestedParam.key) }}
                    </div>
                    <div class="main-table__col main-table__col--value">
                      {{ nestedParam.value.reporting_period }}
                    </div>
                    <div class="main-table__col main-table__col--value">
                      {{ nestedParam.value.previous_year }}
                    </div>
                    <div class="main-table__col main-table__col--value">
                      {{ nestedParam.value.percent_to_previous_year }}
                    </div>
                    <div
                      v-if="mainStore.forecastStatusDisplay"
                      :class="{'main-table__col--forecast': mainStore.forecastStatusDisplay}"
                      class="main-table__col main-table__col--value"
                    >
                      {{ mainStore.currentForecast.regions?.[row.region]?.[nestedParam.key].prediction_value ?? 0 }}
                    </div>
                  </div>
                </template>
              </template>
            </template>
          </template>
        </template>

        <TableEmptyData v-if="!loading && props.items.length === 0" />

        <TableLoader
          v-if="loading"
          position="absolute"
        />
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
@use "@/assets/style/perfect-scrollbar";

.wrapper {
  background-color: var(--background-01);
  padding: 0 10px 10px 10px;
  border-radius: 10px;
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-sizing: border-box;
}

.main-table {
  display: flex;
  flex-direction: column;
  overflow: hidden;
  flex: 1;

  &__header {
    flex-shrink: 0;

    &--forecast {
      grid-template-columns: 1fr 158px 75px 94px 87px !important;

      &.main-table__header--with-scroll {
        margin-right: 24px;
      }
    }

    &--with-scroll {
      margin-right: 28px;
    }
  }

  &__body {
    position: relative;
    flex: 1;
    min-height: 0;
    background-color: var(--background-bg-white);
  }

  &__row {
    display: grid;
    grid-template-columns: 32px 1fr 158px 75px 94px;
    flex-shrink: 0;

    &--nested {
      grid-template-columns: 32px 32px 1fr 158px 75px 94px;

      &-district {
        grid-template-columns: 32px 32px 32px 1fr 158px 75px 94px;

        &-forecast {
          grid-template-columns: 32px 32px 32px 1fr 158px 75px 94px 87px;
        }
      }
    }

    &--data-district {
      grid-template-columns: 32px 32px 1fr 158px 75px 94px;

      &-forecast {
        grid-template-columns: 32px 32px 1fr 158px 75px 94px 87px;
      }
    }

    &--forecast {
      grid-template-columns: 32px 1fr 158px 75px 94px 87px;
    }

    &--forecast-nested {
      grid-template-columns: 32px 32px 1fr 158px 75px 94px 87px !important;
    }

    &--title {
      grid-template-columns: 1fr;
      border-bottom: 1px solid var(--purple-purple20);

      &-forecast {
        grid-template-columns: 1fr 87px;
      }

      &-nested {
        grid-template-columns: 32px 1fr;

        &-forecast {
          grid-template-columns: 32px 1fr 87px;
        }
      }
    }

    &--data:not(:last-child) {
      border-bottom: 1px solid var(--purple-purple20);
    }

    &--with-scroll {
      margin-right: 24px;
    }

    &--expanded {
      border-bottom: 1px solid var(--purple-purple80);
    }

    &--header {
      position: sticky;
      top: 0;
      z-index: 1;
      grid-template-columns: 1fr 158px 75px 94px;
      border: none;
    }
  }

  &__col {
    font-weight: 400;
    font-size: 14px;
    line-height: 22px;
    padding: 10px 16px;
    background-color: var(--background-bg-white);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;

    &--value {
      text-align: center;
    }

    &--forecast {
      background-color: var(--blue-blue30) !important;
    }

    &--forecast-header {
      padding-top: 20px;
      margin-top: 0 !important;
      background-color: #104AA1 !important;
      color: #FFFFFF !important;
    }

    &--nested {
      padding-left: 10px;
      border-left: 1px solid var(--purple-purple20);
    }

    &--title {
      font-size: 16px;
      font-weight: 500;
      line-height: 22px;
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 16px;
    }

    &--spacer {
      padding: 0;
    }

    &--expandable {
      display: flex;
      align-items: center;
      gap: 8px;
    }

    &--header {
      font-weight: 500;
      font-size: 14px;
      line-height: 22px;
      color: var(--text-text-main);
      background-color: var(--background-01);
      margin-top: 10px;
    }
  }
}

.expand-button {
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.2s ease;
  flex-shrink: 0;
  transform: rotate(180deg);

  &--nested {
    width: 16px;
    height: 16px;

  }

  &--expanded {
    transform: rotate(0);
  }

  &:hover {
    opacity: 0.7;
  }
}
</style>